import storage from "../storage";
import {CredentialError} from "@/errors/errors";
import service from "./http";
import serve from "@/utils/serve";
import {workspaceOptions} from "./SystemService";
import {applyLanguage} from "@/utils/language";
//O `firebase` default é o namespace do SDK v8, de onde saem os provedores e as credenciais
import firebase, {auth} from "@/firebase";

class AuthService {
    async login({email, password}: { email: string, password: string }) {
        try {
            const cred = await auth.signInWithEmailAndPassword(email, password);
            const token = await cred.user?.getIdToken();
            if (!token) {
                throw new Error("No Firebase token received");
            }

            // 2️⃣ Backend valida token + convite
            await this.sync(token);
        } catch (err) {
            //Só os erros do FIREBASE: os do servidor já chegam tipados do `sync`, e traduzi-los
            //de novo aqui seria um segundo lugar decidindo o que um 403 quer dizer
            if (err instanceof Error && "code" in err) {
                const code = (err as any).code;
                if (["auth/internal-error", "auth/user-not-found", "auth/wrong-password",
                     "auth/invalid-credential"].includes(code)) {
                    throw new CredentialError(err)
                }
            }
            throw err;
        }
    }


    /**
     * Quem é o usuário desta sessão, perguntado ao servidor e guardado.
     *
     * Um dono só, com dois chamadores. O **login** passa o token recém-obtido de propósito: logo
     * depois do `signIn` o `auth.currentUser` ainda pode estar nulo ou apontando para a sessão
     * anterior, e o interceptor mandaria o token errado — era o 401 que este parâmetro resolve.
     * A **inicialização** chama sem token, porque ali a sessão já está restaurada.
     *
     * Existe porque a cópia do usuário nascia só dentro do `login()`: desde que a sessão passou
     * a morar no Firebase, ela e esta cópia vivem em armazenamentos diferentes, e quem limpava
     * os dados do site ficava logado e sem o avatar — logo, sem o "Sair".
     */
    async sync(token?: string) {
        //Pelo `serve`, como todo serviço do app: é ele que transforma a recusa HTTP em erro
        //tipado. Sem ele, o 403 do convite pendente chegava à tela como um erro cru do axios e
        //virava "Erro inesperado" — a frase certa existia e ninguém a alcançava
        const user = await serve(async () => await service.post("auth/sync", {},
            token ? {headers: {Authorization: `Bearer ${token}`}} : undefined));
        storage.user = user;
        //A conta pode trazer um idioma preferido escolhido em outro aparelho
        applyLanguage(user.language);
        return user;
    }

    /**
     * O idioma preferido da conta — um dos que o app tem, ou `null` para voltar ao do navegador.
     * Grava no servidor, guarda a conta devolvida e aplica: a tela muda de idioma na hora.
     */
    async setLanguage(language: string | null) {
        const user = await serve(async () => await service.patch("auth/me", {language}));
        storage.user = user;
        applyLanguage(user.language);
        return user;
    }

    /**
     * Entrar com o Google.
     *
     * O `sync` continua valendo depois: **entrar por um provedor social não é atalho para
     * dentro**. Quem está pendente leva o mesmo 403 e vê a mesma frase de convite.
     *
     * De brinde, resolve o problema do nome — `displayName` e `photoURL` vêm preenchidos, e o
     * `auth/sync` os guarda no documento, de onde as listas de gente os leem.
     */
    async signInWithGoogle() {
        const provider = new firebase.auth.GoogleAuthProvider();
        //Pergunta a conta toda vez, em vez de entrar com a última usada em silêncio: numa
        //máquina compartilhada, "entrar" que entra com outra pessoa é pior que um clique a mais
        provider.setCustomParameters({prompt: 'select_account'});
        const cred = await auth.signInWithPopup(provider);
        const token = await cred.user?.getIdToken();
        if (!token) throw new Error("No Firebase token received");
        await this.sync(token);
    }

    /**
     * Cria a conta e **guarda o nome**.
     *
     * O `updateProfile` é o que faltava: o nome era pedido no formulário, passado até aqui e
     * descartado — nem ia para o Firebase, nem para o servidor. Daí o avatar de todo mundo
     * mostrar a inicial do e-mail.
     *
     * Antes do `sendEmailVerification`, de propósito: o e-mail de verificação sai com o nome de
     * quem se cadastrou, e não com o endereço.
     */
    async signup({name, email, password}: { name: string; email: string; password: string; }) {
        const cred = await auth.createUserWithEmailAndPassword(email, password);
        await cred.user?.updateProfile({displayName: name});
        await cred.user?.sendEmailVerification();
    }

    /** O e-mail de redefinição. Quem esquecia a senha não tinha saída nenhuma. */
    async sendPasswordReset(email: string) {
        await auth.sendPasswordResetEmail(email);
    }

    /**
     * Troca a senha, provando antes que é quem diz ser.
     *
     * O `reauthenticate` não é zelo nosso: o Firebase recusa `updatePassword` numa sessão
     * antiga, e é por isso que a tela pede a senha atual.
     */
    async changePassword(currentPassword: string, newPassword: string) {
        const user = auth.currentUser!;
        const credential = firebase.auth.EmailAuthProvider.credential(user.email!, currentPassword);
        await user.reauthenticateWithCredential(credential);
        await user.updatePassword(newPassword);
    }

    /**
     * Grava o perfil e manda o servidor relê-lo.
     *
     * **A ordem é obrigatória.** O `getIdToken(true)` força o refresh: sem ele o token ainda
     * carrega o nome antigo, e o `sync` seguinte regravaria no documento exatamente o que
     * acabou de ser substituído.
     */
    async updateProfile(profile: {displayName?: string, photoURL?: string}) {
        await auth.currentUser!.updateProfile(profile);
        await auth.currentUser!.getIdToken(true);
        return await this.sync();
    }

    /** Entrou por e-mail e senha? Quem veio do Google não tem senha para trocar. */
    hasPassword(): boolean {
        return (auth.currentUser?.providerData || [])
            .some(provider => provider?.providerId === 'password');
    }

    async sendEmailVerification() {
        await auth.currentUser!.sendEmailVerification();
    }

    async logout() {
        storage.credentials = null;
        storage.user = null;
        //A lista de ambientes é de QUEM sai: guardada para a sessão, ela apareceria no menu de quem
        //entrar em seguida na mesma aba
        workspaceOptions.value = null;
        //O idioma preferido também é de quem sai: a tela de entrada volta ao do navegador
        applyLanguage();
        await auth.signOut();
    }
}

export default AuthService